import { useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import {
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';

import {
  Configuration,
  DetailedShipList,
  type GetShiplists200Response,
  ShipList,
  ShiplistApi,
} from '@ais-ui/api-spec/config';
import { getBasePath } from './getAISBasePath';
import { useApiClientWrapper } from './apiClientWrapper';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';

const useShiplistApi = (): ShiplistApi => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();
  let basePath = getBasePath();
  let configuration: Configuration;
  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/config`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}/aisweb/api/config`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const shiplistApi = useMemo(
    () => new ShiplistApi(configuration),
    [configuration]
  );
  // The wrapper is used to get error messages from the backend, as they are otherwise thrown out
  return apiClientWrapper(shiplistApi) as ShiplistApi;
};

export const useFetchAllShiplists =
  (): UseQueryResult<GetShiplists200Response> => {
    const shiplistApi = useShiplistApi();

    return useQuery(
      ['FetchAllShiplists'],
      async () => {
        return await shiplistApi.getShiplists();
      },
      {
        staleTime: 30000, // Consider data fresh for 30 seconds
        refetchInterval: false,
        refetchOnWindowFocus: false,
      }
    );
  };

export const useGetShiplist = (
  id: string
): UseQueryResult<DetailedShipList> => {
  const shiplistApi = useShiplistApi();
  return useQuery(['FetchShiplist', id], async () => {
    return await shiplistApi.getShiplist({ id });
  });
};

export const useCreateShiplist = (): UseMutationResult<
  void,
  { response?: any },
  DetailedShipList,
  unknown
> => {
  const shiplistApi = useShiplistApi();
  // const shiplistApi = useShiplistApi();
  const queryClient = useQueryClient();

  return useMutation(
    (shiplist: ShipList) => {
      return shiplistApi.addShiplist({ json: shiplist });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['FetchAllShiplists']),
    }
  );
};

export const useUpdateShiplist = (): UseMutationResult<
  void,
  { response?: any },
  DetailedShipList,
  unknown
> => {
  const shiplistApi = useShiplistApi();
  const queryClient = useQueryClient();

  return useMutation(
    (shiplist: ShipList) => {
      return shiplistApi.updateShiplist({ detailedShipList: shiplist });
    },
    {
      onSuccess: (data, shiplist) => {
        queryClient.invalidateQueries(['FetchAllShiplists']);
        queryClient.invalidateQueries(['FetchShiplist', shiplist.id]);
      },
    }
  );
};

export const useDeleteShiplist = (): UseMutationResult<
  void,
  Error,
  string,
  unknown
> => {
  const shiplistApi = useShiplistApi();
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => {
      return shiplistApi.shiplistIdDelete({ id: id });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['FetchAllShiplists']),
    }
  );
};
